import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@auth0/auth0-angular';
import { inviteApiActions } from '@mkp/account-membership/actions';
import { authApiActions } from '@mkp/auth/actions';
import { selectInterfaceLanguage } from '@mkp/core/state';
import { languageEffectsActions } from '@mkp/core/state/actions';
import { incompleteUserActions } from '@mkp/onboarding/feature-incomplete/actions';
import { ActionState, LanguageMapper } from '@mkp/shared/models';
import { MessageError, isMessageError } from '@mkp/shared/data-access';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { catch304 } from '@mkp/shared/util-api';
import { AppConfig } from '@mkp/shared/util-app-config';
import { QueryOptions } from '@mkp/shared/util-pagination';
import { selectRouteParam, selectRouteQueryParam } from '@mkp/shared/util-router';
import { UserApi, UserResource } from '@mkp/user/data-access';
import { UnsubscribePageActions } from '@mkp/user/feature-unsubscribe/actions';
import { castToUserDisplayType, getUserFullName } from '@mkp/user/models';
import { selectLoggedInUser, selectUserById } from '@mkp/user/state-logged-in/selectors';
import { userApiActions, userDetailsActions, userListActions } from '@mkp/user/state/actions';
import { vacancyEditActions } from '@mkp/vacancy/feature-vacancy-edit/actions';
import { FullviewService } from '@mkp/shared/util-zendesk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  OperatorFunction,
  catchError,
  exhaustMap,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

@Injectable()
export class LoggedInUserEffects {
  private actions$ = inject(Actions);
  private userApi = inject(UserApi);
  private notificationService = inject(SnackbarService);
  private snackBar = inject(SnackbarService);
  private router = inject(Router);
  private store = inject(Store);
  private userResource = inject(UserResource);
  private fullviewService = inject(FullviewService);

  readonly fetchLoggedInUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authApiActions.fetchAccessTokenSuccess),
      switchMap(({ actorId }) =>
        this.userApi.readAndMap(actorId).pipe(
          map((user) => userApiActions.readLoggedInUserSuccess({ user })),
          catchError(
            (error: unknown) =>
              isUserError(error) && of(userApiActions.readLoggedInUserFailure({ error }))
          )
        )
      )
    )
  );

  readonly setFullviewUserData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.readLoggedInUserSuccess),
        map(({ user }) => ({
          userId: user.id,
          name: getUserFullName(user),
          email: user.email,
        })),
        tap((fullviewUserData) => this.fullviewService.setFullviewUserData(fullviewUserData))
      ),
    { dispatch: false }
  );

  readonly listUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userListActions.init),
      this.fetchUsers(userApiActions.listUsersSuccess, userApiActions.listUsersFailure)
    )
  );

  readonly initUserDetailsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDetailsActions.initUserDetailsPage),
      concatLatestFrom(() => this.store.select(selectRouteParam('id'))),
      map(([, id]) => id),
      filter(Boolean),
      concatLatestFrom((id) => this.store.select(selectUserById(id))),
      filter(([, user]) => !user),
      map(([id]) => ({ query: { filter: `id==${id}` } })),
      this.fetchUsers(userApiActions.listUsersSuccess, userApiActions.listUsersFailure)
    )
  );

  readonly createPasswordlessUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(inviteApiActions.redeemInviteForNonExistingUser),
      map(({ token, email }) => ({ token, email })),
      concatLatestFrom(() => [
        this.store.select(selectRouteQueryParam('customerType')),
        this.store.select(selectInterfaceLanguage),
      ]),
      exhaustMap(([{ token, email }, displayType, language]) => {
        return this.userResource
          .createPasswordlessUser({
            email,
            displayType: castToUserDisplayType(displayType),
            language: LanguageMapper.toCode(language),
          })
          .pipe(
            map(() =>
              userApiActions.createPasswordlessUserSuccess({
                token,
                email,
              })
            ),
            catchError((error: unknown) =>
              of(userApiActions.createPasswordlessUserFailure({ error }))
            )
          );
      })
    )
  );

  readonly createPasswordlessUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.createPasswordlessUserFailure),
        tap(() => {
          this.snackBar.showError('REQUEST_TIMEOUT_MESSAGE');
          this.router.navigate([AppConfig.routes.invalidInvite]);
        })
      ),
    { dispatch: false }
  );

  readonly loadMoreUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userListActions.loadMore),
      this.fetchUsers(userApiActions.loadMoreUsersSuccess, userApiActions.loadMoreUsersFailure)
    )
  );

  readonly updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        userDetailsActions.updateUser,
        userDetailsActions.updateUserProfile,
        languageEffectsActions.appLanguageSwitchedForUser
      ),
      switchMap(({ updateUser, notification, redirectUrl }) => {
        return this.userApi.patch(updateUser).pipe(
          tap(() => {
            if (notification) {
              this.notificationService.show(notification);
            }
          }),
          map((user) => userApiActions.updateUserSuccess({ id: user.id, changes: user })),
          tap(() => {
            if (redirectUrl) {
              this.router.navigate([redirectUrl]);
            }
          }),
          catch304(() => {
            this.snackBar.show('GENERIC.SNACKBAR.NO_CHANGES_MESSAGE');
            return userApiActions.updateUserNoChanges();
          }),
          catchError((error: unknown) => {
            return isUserError(error) && of(userApiActions.updateUserFailure({ error }));
          })
        );
      })
    )
  );

  readonly resetPassword$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userDetailsActions.resetPassword),
        switchMap(({ email, notification }) => {
          return this.userApi.resetPassword(email).pipe(
            tap(() => this.notificationService.show(notification)),
            map(() => userApiActions.resetPasswordSuccess()),
            catchError((error: unknown) => {
              this.notificationService.show('USER_ACCOUNT.USER.ERROR.RESET_PASSWORD', {
                state: ActionState.Error,
              });
              return isUserError(error) && of(userApiActions.resetPasswordFailure({ error }));
            })
          );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  readonly unsubscribe$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UnsubscribePageActions.unsubscribeLoggedInUser),
        switchMap(() => this.store.select(selectLoggedInUser).pipe(filter(Boolean), take(1))),
        switchMap((user) => {
          return this.userApi
            .patch(
              { id: user.id, settings: { marketingConsent: false }, _version: user._version },
              1
            )
            .pipe(
              tap(() => this.notificationService.show('UNSUBSCRIBE.SUCCESS.NOTIFICATION')),
              map((user) => userApiActions.updateUserSuccess({ id: user.id, changes: user })),
              catch304(() => userApiActions.updateUserNoChanges()),
              catchError((error: unknown) => {
                this.notificationService.show('UNSUBSCRIBE.FAILURE.NOTIFICATION', {
                  state: ActionState.Error,
                });
                return isUserError(error) && of(userApiActions.updateUserFailure({ error }));
              })
            );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  updateChatGptContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(vacancyEditActions.updatePublicationAiUsageConsent),
      withLatestFrom(this.store.select(selectLoggedInUser)),
      filter(([, user]) => !!user),
      switchMap(([{ consent }, user]) =>
        this.userApi
          .patch({
            id: (user as User).id,
            _version: (user as User)._version,
            settings: {
              ...user.settings,
              publicationAiUsageConsent: consent,
            },
          })
          .pipe(
            map((user) => userApiActions.updateUserSuccess({ id: user.id, changes: user })),
            catchError((error: unknown) => {
              return isUserError(error) && of(userApiActions.updateUserFailure({ error }));
            })
          )
      )
    )
  );

  readonly updateIncompleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(incompleteUserActions.updateUser),
      switchMap(({ user }) => {
        return this.userApi.patch(user).pipe(
          map((user) => userApiActions.updateIncompleteUserSuccess({ user })),
          catchError((error: unknown) => {
            return isUserError(error) && of(userApiActions.updateIncompleteUserFailure({ error }));
          })
        );
      })
    )
  );

  readonly updateIncompleteUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.updateIncompleteUserSuccess),
        tap(() => {
          // Redirect to dashboard after successful update
          this.router.navigate([AppConfig.routes.dashboard]);
          this.snackBar.show('INCOMPLETE_ONBOARDING.UPDATE.SUCCESS');
        })
      ),
    { dispatch: false }
  );

  readonly updateIncompleteUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.updateIncompleteUserFailure),
        tap(() => this.snackBar.showError('INCOMPLETE_ONBOARDING.UPDATE.ERROR'))
      ),
    { dispatch: false }
  );

  private fetchUsers(
    successAction: FetchUsersSuccessActionCreator,
    failureAction: FetchUsersFailureActionCreator
  ): OperatorFunction<{ query: QueryOptions }, FetchUsersAction> {
    return switchMap(({ query }) => {
      const options = query || { limit: 25 };
      return this.userApi.listAndMap(options).pipe(
        map(({ _embedded, _links, totalCount, filter }) =>
          successAction({
            users: _embedded.results,
            _links,
            totalCount,
            filter,
          })
        ),
        catchError((error: unknown) => isUserError(error) && of(failureAction({ error })))
      );
    });
  }

  readonly resetMfa$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userDetailsActions.resetMfa),
        switchMap(({ userId, userFullName }) => {
          return this.userApi.resetMfa(userId).pipe(
            tap(() =>
              this.notificationService.show('USER_ACCOUNT.USER.MESSAGE.RESET_MFA.SUCCESS', {
                translationParams: { userFullName },
              })
            ),
            map(() => userApiActions.resetMfaSuccess()),
            catchError((error: unknown) => {
              this.notificationService.show('USER_ACCOUNT.USER.MESSAGE.RESET_MFA.ERROR', {
                state: ActionState.Error,
              });
              return isUserError(error) && of(userApiActions.resetMfaFailure({ error }));
            })
          );
        })
      ),
    { useEffectsErrorHandler: false }
  );
}

type FetchUsersSuccessActionCreator =
  typeof userApiActions.listUsersSuccess | typeof userApiActions.loadMoreUsersSuccess;
type FetchUsersFailureActionCreator =
  typeof userApiActions.listUsersFailure | typeof userApiActions.loadMoreUsersFailure;
type FetchUsersAction = ReturnType<FetchUsersSuccessActionCreator | FetchUsersFailureActionCreator>;

const isUserError = (error: unknown): error is MessageError => isMessageError(error, 'UserEffects');
